import { useEffect } from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  BOUserTypeEnum,
  FullBOUserResponseModel,
  MemberHistoryDetailsModel,
} from 'api/adminApiClient';
import HistoryLog from '../../HistoryLog';
import HistoryField from '../../HistoryField';
import { getWebsiteLink, getWebsiteName } from 'utils/urlUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getAllAdmins } from 'redux/reducers/adminSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { adminUsersNameMapSelector } from 'redux/selectors/adminSelectors';
import {
  getMyBasePayTeamDisplayName,
  hasMyBasePayTeamFieldChanged,
  resolveMyBasePayTeam,
} from 'utils/myBasePayTeamUtils';

import styles from '../../HistoryCard.module.less';

type Props = {
  historyDetails: MemberHistoryDetailsModel;
  historyId: string;
};

export default function GeneralInformationDetailsCard(props: Props) {
  const { historyDetails, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const adminNamesMap = useAppSelector(adminUsersNameMapSelector);

  useEffect(() => {
    if (isAdmin) {
      dispatch(getAllAdmins());
    }
  }, [dispatch, isAdmin]);

  // Owner info checks
  const hasOwnerInfoFirstNameChanged = !isEqual(
    oldData?.ownerInfo?.firstName || '',
    newData?.ownerInfo?.firstName || '',
  );
  const hasOwnerInfoLastNameChanged = !isEqual(
    oldData?.ownerInfo?.lastName || '',
    newData?.ownerInfo?.lastName || '',
  );
  const hasOwnerInfoEmailChanged = !isEqual(
    oldData?.ownerInfo?.email || '',
    newData?.ownerInfo?.email || '',
  );
  const hasOwnerInfoChanged =
    hasOwnerInfoFirstNameChanged ||
    hasOwnerInfoLastNameChanged ||
    hasOwnerInfoEmailChanged;

  // Best contact checks
  const hasBestContactFirstNameChanged = !isEqual(
    oldData?.bestContact?.firstName || '',
    newData?.bestContact?.firstName || '',
  );
  const hasBestContactLastNameChanged = !isEqual(
    oldData?.bestContact?.lastName || '',
    newData?.bestContact?.lastName || '',
  );
  const hasBestContactEmailChanged = !isEqual(
    oldData?.bestContact?.email || '',
    newData?.bestContact?.email || '',
  );
  const hasBestContactChanged =
    hasBestContactFirstNameChanged ||
    hasBestContactLastNameChanged ||
    hasBestContactEmailChanged;

  // General info checks
  const hasBusinessNameChanged = !isEqual(
    oldData?.businessInfo?.name || '',
    newData?.businessInfo?.name || '',
  );
  const hasBusinessEmailChanged = !isEqual(
    oldData?.businessInfo?.businessEmail || '',
    newData?.businessInfo?.businessEmail || '',
  );
  const hasBusinessPhoneNumberChanged = !isEqual(
    oldData?.businessInfo?.phoneNumber || '',
    newData?.businessInfo?.phoneNumber || '',
  );
  const hasBusinessIndustriesChanged = !isEqual(
    (oldData?.businessInfo?.industries || []).map((industry) => industry.name),
    (newData?.businessInfo?.industries || []).map((industry) => industry.name),
  );
  const hasBusinessStateChanged = !isEqual(
    oldData?.businessInfo?.location?.state ||
      oldData?.businessInfo?.location?.internationalStateName ||
      '',
    newData?.businessInfo?.location?.state ||
      newData?.businessInfo?.location?.internationalStateName ||
      '',
  );
  const hasBusinessCityChanged = !isEqual(
    oldData?.businessInfo?.location?.city ||
      oldData?.businessInfo?.location?.internationalCityName ||
      '',
    newData?.businessInfo?.location?.city ||
      newData?.businessInfo?.location?.internationalCityName ||
      '',
  );
  const hasBusinessCountryChanged = !isEqual(
    oldData?.businessInfo?.location?.countryName || '',
    newData?.businessInfo?.location?.countryName || '',
  );
  const hasBusinessAddressChanged = !isEqual(
    oldData?.businessInfo?.location?.address || '',
    newData?.businessInfo?.location?.address || '',
  );
  const hasBusinessZipChanged = !isEqual(
    oldData?.businessInfo?.location?.zip || '',
    newData?.businessInfo?.location?.zip || '',
  );
  const hasWebsiteChanged = !isEqual(
    oldData?.website || '',
    newData?.website || '',
  );
  const hasAssignmentLocationsChanged = !isEqual(
    (oldData?.businessInfo?.assignmentLocations || []).map(
      ({ name, stateCode }) => ({ name, stateCode }),
    ),
    (newData?.businessInfo?.assignmentLocations || []).map(
      ({ name, stateCode }) => ({ name, stateCode }),
    ),
  );
  const oldMyBasePayTeam = resolveMyBasePayTeam(oldData);
  const newMyBasePayTeam = resolveMyBasePayTeam(newData);
  const hasMbpClientSuccessPartnerChanged = hasMyBasePayTeamFieldChanged(
    oldMyBasePayTeam,
    newMyBasePayTeam,
    'mbpClientSuccessPartnerUserId',
    'mbpClientSuccessPartner',
  );
  const hasMspSalesRepresentativeChanged = hasMyBasePayTeamFieldChanged(
    oldMyBasePayTeam,
    newMyBasePayTeam,
    'mspSalesRepresentativeUserId',
    'mspSalesRepresentative',
  );
  const hasMbpBillingRepresentativeChanged = hasMyBasePayTeamFieldChanged(
    oldMyBasePayTeam,
    newMyBasePayTeam,
    'mbpBillingRepresentativeUserId',
    'mbpBillingRepresentative',
  );
  const hasRecipientEmail1Changed = !isEqual(
    oldData?.activityReportRecipients?.recipientEmail1 || '',
    newData?.activityReportRecipients?.recipientEmail1 || '',
  );
  const hasRecipientEmail2Changed = !isEqual(
    oldData?.activityReportRecipients?.recipientEmail2 || '',
    newData?.activityReportRecipients?.recipientEmail2 || '',
  );
  const hasRecipientEmail3Changed = !isEqual(
    oldData?.activityReportRecipients?.recipientEmail3 || '',
    newData?.activityReportRecipients?.recipientEmail3 || '',
  );

  const hasActivityReportRecipientsChanged =
    hasRecipientEmail1Changed ||
    hasRecipientEmail2Changed ||
    hasRecipientEmail3Changed;
  const hasAssignedInternalContactsChanged =
    hasMbpClientSuccessPartnerChanged ||
    hasMspSalesRepresentativeChanged ||
    (isAdmin && hasMbpBillingRepresentativeChanged);
  const hasGeneralInfoChanged =
    hasBusinessNameChanged ||
    hasBusinessEmailChanged ||
    hasBusinessPhoneNumberChanged ||
    hasBusinessIndustriesChanged ||
    hasBusinessStateChanged ||
    hasBusinessCityChanged ||
    hasBusinessCountryChanged ||
    hasBusinessAddressChanged ||
    hasBusinessZipChanged ||
    hasWebsiteChanged ||
    hasAssignmentLocationsChanged;

  const renderCompanyRepresentative = (profile?: FullBOUserResponseModel) => {
    if (!profile) return null;

    const { ownerInfo, boUserType } = profile;

    const sectionName =
      boUserType === BOUserTypeEnum.SelfEmployed
        ? 'owner-information'
        : 'company-representative';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>
              {boUserType === BOUserTypeEnum.SelfEmployed
                ? 'Owner information'
                : 'Company representative'}
            </div>
          </Col>

          {hasOwnerInfoFirstNameChanged && (
            <HistoryField
              title="First name"
              value={ownerInfo?.firstName}
              dataId={`${sectionName}-firstName-field`}
            />
          )}

          {hasOwnerInfoLastNameChanged && (
            <HistoryField
              title="Last name"
              value={ownerInfo?.lastName}
              dataId={`${sectionName}-lastName-field`}
            />
          )}

          {hasOwnerInfoEmailChanged && (
            <HistoryField
              title="Email"
              value={ownerInfo?.email}
              dataId={`${sectionName}-email-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderBestContact = (profile?: FullBOUserResponseModel) => {
    if (!profile) return null;

    const { bestContact } = profile;
    const sectionName = 'best-point-of-contact';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Best point of contact</div>
          </Col>

          {hasBestContactFirstNameChanged && (
            <HistoryField
              title="First name"
              value={bestContact?.firstName}
              dataId={`${sectionName}-firstName-field`}
            />
          )}

          {hasBestContactLastNameChanged && (
            <HistoryField
              title="Last name"
              value={bestContact?.lastName}
              dataId={`${sectionName}-lastName-field`}
            />
          )}

          {hasBestContactEmailChanged && (
            <HistoryField
              title="Email for communications"
              value={bestContact?.email}
              dataId={`${sectionName}-email-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderBusinessInfo = (profile?: FullBOUserResponseModel) => {
    if (!profile) return null;

    const { businessInfo, website } = profile;
    const sectionName = 'business-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Business information</div>
          </Col>
          {hasBusinessNameChanged && (
            <HistoryField
              title="Business name"
              value={businessInfo?.name}
              dataId={`${sectionName}-name-field`}
            />
          )}
          {hasBusinessEmailChanged && (
            <HistoryField
              title="Business email"
              value={businessInfo?.businessEmail}
              dataId={`${sectionName}-business-name-field`}
            />
          )}
          {hasBusinessPhoneNumberChanged && (
            <HistoryField
              title="Business phone number"
              value={businessInfo?.phoneNumber}
              dataId={`${sectionName}-phone-number-field`}
            />
          )}
          {hasBusinessIndustriesChanged && (
            <HistoryField
              title="Industry"
              value={businessInfo?.industries
                ?.map((industry) => industry.name)
                .join(', ')}
              dataId={`${sectionName}-industry-field`}
            />
          )}

          {hasBusinessCountryChanged && (
            <HistoryField
              title="Country"
              value={businessInfo?.location?.countryName}
              dataId={`${sectionName}-country-name-field`}
            />
          )}

          {hasBusinessStateChanged && (
            <HistoryField
              title="State"
              value={
                businessInfo?.location?.state ||
                businessInfo?.location?.internationalStateName
              }
              dataId={`${sectionName}-state-name-field`}
            />
          )}

          {hasBusinessCityChanged && (
            <HistoryField
              title="City"
              value={
                businessInfo?.location?.city ||
                businessInfo?.location?.internationalCityName
              }
              dataId={`${sectionName}-city-field`}
            />
          )}

          {hasBusinessAddressChanged && (
            <HistoryField
              title="Address"
              value={businessInfo?.location?.address}
              dataId={`${sectionName}-address-field`}
            />
          )}
          {hasBusinessZipChanged && (
            <HistoryField
              title="ZIP code"
              value={businessInfo?.location?.zip}
              dataId={`${sectionName}-zip-field`}
            />
          )}
          {hasAssignmentLocationsChanged && (
            <HistoryField
              title="States for placements"
              value={businessInfo?.assignmentLocations
                ?.map((location) => location.name)
                .join(', ')}
              dataId={`${sectionName}-assignment-locations-field`}
            />
          )}
          {hasWebsiteChanged && (
            <HistoryField
              title="Website"
              value={
                website && (
                  <a
                    href={getWebsiteLink(website)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getWebsiteName(website)}
                  </a>
                )
              }
              dataId={`${sectionName}-website-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderAssignedInternalContacts = (
    profile?: FullBOUserResponseModel,
  ) => {
    if (!profile) return null;

    const sectionName = 'assigned-internal-contacts';
    const myBasePayTeam = resolveMyBasePayTeam(profile);

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Assigned Internal Contacts</div>
          </Col>

          {hasMbpClientSuccessPartnerChanged && (
            <HistoryField
              title="MBP Client Success Partner"
              value={getMyBasePayTeamDisplayName(
                myBasePayTeam,
                'mbpClientSuccessPartnerUserId',
                'mbpClientSuccessPartner',
                adminNamesMap,
              )}
              dataId={`${sectionName}-mbp-client-success-partner-field`}
            />
          )}

          {hasMspSalesRepresentativeChanged && (
            <HistoryField
              title="MSP Sales Representative"
              value={getMyBasePayTeamDisplayName(
                myBasePayTeam,
                'mspSalesRepresentativeUserId',
                'mspSalesRepresentative',
                adminNamesMap,
              )}
              dataId={`${sectionName}-msp-sales-representative-field`}
            />
          )}

          {isAdmin && hasMbpBillingRepresentativeChanged && (
            <HistoryField
              title="MBP Billing Representative"
              value={getMyBasePayTeamDisplayName(
                myBasePayTeam,
                'mbpBillingRepresentativeUserId',
                'mbpBillingRepresentative',
                adminNamesMap,
              )}
              dataId={`${sectionName}-mbp-billing-representative-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderActivityReportRecipients = (
    profile?: FullBOUserResponseModel,
  ) => {
    if (!profile) return null;

    const { activityReportRecipients } = profile;
    const sectionName = 'activity-report-recipients';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Activity Report Recipients</div>
          </Col>

          {hasRecipientEmail1Changed && (
            <HistoryField
              title="Activity Report Recipient 1"
              value={activityReportRecipients?.recipientEmail1}
              dataId={`${sectionName}-recipient-1-field`}
            />
          )}

          {hasRecipientEmail2Changed && (
            <HistoryField
              title="Activity Report Recipient 2"
              value={activityReportRecipients?.recipientEmail2}
              dataId={`${sectionName}-recipient-2-field`}
            />
          )}

          {hasRecipientEmail3Changed && (
            <HistoryField
              title="Activity Report Recipient 3"
              value={activityReportRecipients?.recipientEmail3}
              dataId={`${sectionName}-recipient-3-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderGeneralInfo = (profile?: FullBOUserResponseModel) => {
    return (
      <>
        {hasOwnerInfoChanged && <>{renderCompanyRepresentative(profile)}</>}

        {hasBestContactChanged && <>{renderBestContact(profile)}</>}

        {hasGeneralInfoChanged && <>{renderBusinessInfo(profile)}</>}

        {hasAssignedInternalContactsChanged && (
          <>{renderAssignedInternalContacts(profile)}</>
        )}

        {hasActivityReportRecipientsChanged && (
          <>{renderActivityReportRecipients(profile)}</>
        )}
      </>
    );
  };

  if (
    !hasBestContactChanged &&
    !hasOwnerInfoChanged &&
    !hasGeneralInfoChanged &&
    !hasActivityReportRecipientsChanged &&
    !hasAssignedInternalContactsChanged
  ) {
    return null;
  }

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderGeneralInfo(oldData)}
      newData={renderGeneralInfo(newData)}
    />
  );
}
